import React, { useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { Upload, Save, Mail, User } from 'lucide-react';
import AdminHeader from '../../components/admin/common/AdminHeader';
import AdminBreadcrumb from '../../components/admin/common/AdminBreadcrumb';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../config/firebase';

const Profile: React.FC = () => {
  const { user, updateUserProfile } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [displayName, setDisplayName] = useState(user?.displayName || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setIsSubmitting(true);
      setError(null);

      // Create a storage reference
      const storageRef = ref(storage, `profile-images/${user?.id}/${file.name}`);
      
      // Upload file
      await uploadBytes(storageRef, file);
      
      // Get download URL
      const photoURL = await getDownloadURL(storageRef);
      
      // Update profile
      await updateUserProfile({ photoURL });
    } catch (err) {
      console.error('Error uploading image:', err);
      setError('Failed to upload image');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!displayName.trim()) return;

    try {
      setIsSubmitting(true);
      setError(null);
      await updateUserProfile({ displayName });
      setIsEditing(false);
    } catch (err) {
      setError('Failed to update profile');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <AdminBreadcrumb items={[{ label: 'Profile' }]} />
      
      <AdminHeader title="Profile Settings" />

      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm p-6">
          {error && (
            <div className="mb-6 p-4 bg-red-50 text-red-600 rounded-lg">
              {error}
            </div>
          )}

          <div className="space-y-8">
            {/* Profile Image */}
            <div className="flex items-center space-x-6">
              <div className="relative">
                <div className="w-24 h-24 rounded-full bg-brand-blue-500 flex items-center justify-center text-white text-3xl overflow-hidden">
                  {user?.photoURL ? (
                    <img 
                      src={user.photoURL} 
                      alt={user.displayName || 'Profile'} 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span>{user?.displayName?.charAt(0) || user?.email?.charAt(0) || 'A'}</span>
                  )}
                </div>
                <label className="absolute bottom-0 right-0 p-1.5 bg-white rounded-full shadow-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    disabled={isSubmitting}
                  />
                  <Upload className="w-4 h-4 text-gray-600" />
                </label>
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-900">Profile Photo</h3>
                <p className="text-sm text-gray-500">
                  Click the upload icon to change your profile photo
                </p>
              </div>
            </div>

            {/* Profile Info */}
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Display Name
                </label>
                {isEditing ? (
                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      className="flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-brand-blue-500"
                      disabled={isSubmitting}
                    />
                    <button
                      type="submit"
                      disabled={isSubmitting || !displayName.trim()}
                      className="p-2 text-white bg-brand-blue-500 rounded-lg hover:bg-brand-blue-600 
                        disabled:bg-gray-300 disabled:cursor-not-allowed"
                    >
                      <Save className="w-5 h-5" />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                    <div className="flex items-center">
                      <User className="w-5 h-5 text-gray-400 mr-2" />
                      <span>{user?.displayName || 'Not set'}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setIsEditing(true)}
                      className="text-sm text-brand-blue-500 hover:text-brand-blue-600"
                    >
                      Edit
                    </button>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <div className="flex items-center p-2 bg-gray-50 rounded-lg">
                  <Mail className="w-5 h-5 text-gray-400 mr-2" />
                  <span>{user?.email}</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Role
                </label>
                <div className="flex items-center p-2 bg-gray-50 rounded-lg">
                  <User className="w-5 h-5 text-gray-400 mr-2" />
                  <span className="capitalize">{user?.role || 'Admin'}</span>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;